import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  activateUser,
  deactivateUser,
  anonymizeUser,
  purgeUser,
  setPurgeEnabled,
  getUserDeletionImpact,
  getAllUsers,
  UserSortField,
  UserSortDir,
  createUser,
  verifyUserEmail,
  UserAdmin,
  UserDeletionImpact,
  CreateUserRequest,
} from '../api.admin';

const getToken = () => localStorage.getItem('token') || '';

const EMPTY_FORM: CreateUserRequest = {
  email: '', password: '', firstName: '', lastName: '', phoneNumber: '', role: 'MANAGER',
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span style={{
    display: 'inline-block', padding: '1px 7px', borderRadius: 3,
    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em',
    background: role === 'ADMIN' ? '#805ad5' : '#38a169', color: '#fff',
  }}>
    {role}
  </span>
);

const StatusText: React.FC<{ ok: boolean; yes: string; no: string }> = ({ ok, yes, no }) => (
  <span style={{ color: ok ? '#276749' : '#9b2c2c', fontWeight: 600, fontSize: '0.8rem' }}>
    {ok ? `✓ ${yes}` : `✗ ${no}`}
  </span>
);

const StatusPill: React.FC<{ ok: boolean; yes: string; no: string }> = ({ ok, yes, no }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: ok ? '#276749' : '#9b2c2c' }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: ok ? '#38a169' : '#e53e3e', flexShrink: 0 }} />
    {ok ? yes : no}
  </span>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const AnonymizedLabel = () => (
  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#718096', background: '#edf2f7', borderRadius: 3, padding: '1px 7px' }}>
    Anonymized
  </span>
);

const formatLastSeen = (ts?: string): string => {
  if (!ts) return 'Never';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 86400 / 30)}mo ago`;
};

const formatLastLogin = (ts?: string): string => (ts ? formatLastSeen(ts) : '—');

const SORT_LABELS: Record<UserSortField, string> = { name: 'Name', apartments: 'Apts', lastLogin: 'Last login' };

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateUserRequest>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<UserAdmin | null>(null);
  const [impact, setImpact] = useState<UserDeletionImpact | null>(null);
  const [impactError, setImpactError] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [savingPurgeSwitch, setSavingPurgeSwitch] = useState(false);
  // Bumped whenever a delete dialog opens or closes, so a response that belongs to an earlier dialog is dropped.
  const dialogSession = useRef(0);

  // Server-side sort; null = the endpoint's default order. The ref lets a refetch after an action keep the order.
  const [sort, setSort] = useState<{ field: UserSortField; dir: UserSortDir } | null>(null);
  const sortRef = useRef(sort);
  // Bumped per request: a slow answer to an earlier sort click must not overwrite a newer one.
  const fetchSeq = useRef(0);

  const fetchUsers = async () => {
    const seq = ++fetchSeq.current;
    // Keep the table on screen while re-sorting; only the very first load shows the spinner.
    setLoading(users.length === 0); setError(null);
    try {
      const loaded = await getAllUsers(getToken(), sortRef.current?.field, sortRef.current?.dir);
      if (seq === fetchSeq.current) setUsers(loaded);
    } catch (err: any) {
      if (seq === fetchSeq.current) setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const applySort = (next: { field: UserSortField; dir: UserSortDir }) => {
    sortRef.current = next;
    setSort(next);
    fetchUsers();
  };

  const clearSort = () => {
    sortRef.current = null;
    setSort(null);
    fetchUsers();
  };

  // First click on a column sorts ascending; clicking the active column flips the direction.
  const toggleSort = (field: UserSortField) =>
    applySort({ field, dir: sort?.field === field && sort.dir === 'asc' ? 'desc' : 'asc' });

  const sortHeader = (field: UserSortField) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      {SORT_LABELS[field]}{sort?.field === field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  );

  const notify = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    try {
      await createUser(formData, getToken());
      notify('User created'); setShowForm(false); setFormData(EMPTY_FORM); fetchUsers();
    } catch (err: any) { setError(err.response?.data?.message || err.message || 'Failed to create user'); }
  };

  const handleActivate   = async (id: number) => { try { await activateUser(id, getToken());   notify('Activated');   fetchUsers(); } catch (err: any) { setError(err.response?.data?.message || err.message); } };
  const handleDeactivate = async (id: number) => { try { await deactivateUser(id, getToken()); notify('Deactivated'); fetchUsers(); } catch (err: any) { setError(err.response?.data?.message || err.message); } };
  const handleVerify     = async (id: number) => { try { await verifyUserEmail(id, getToken()); notify('Email verified'); fetchUsers(); } catch (err: any) { setError(err.response?.data?.message || err.message); } };
  const handleDelete = async (u: UserAdmin) => {
    const session = ++dialogSession.current;
    setDeleteTarget(u); setImpact(null); setImpactError(null); setConfirmEmail(''); setDeleteError(null);
    setSavingPurgeSwitch(false);
    try {
      const loaded = await getUserDeletionImpact(u.id, getToken());
      if (session !== dialogSession.current) return;
      setImpact(loaded);
    } catch (err: any) {
      if (session !== dialogSession.current) return;
      setImpactError(err.response?.data?.message || err.message || 'Failed to load deletion impact');
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    dialogSession.current++;
    setDeleteTarget(null);
  };

  const togglePurgeEnabled = async (enabled: boolean) => {
    if (savingPurgeSwitch || deleting) return;
    if (enabled && !window.confirm('Allow "Delete everything" on this environment? It permanently deletes a user and all their data, invoices included.')) return;
    const session = dialogSession.current;
    setDeleteError(null);
    setSavingPurgeSwitch(true);
    try {
      await setPurgeEnabled(enabled, getToken());
      if (session !== dialogSession.current) return;
      setImpact(prev => (prev ? { ...prev, purgeEnabled: enabled } : prev));
    } catch (err: any) {
      if (session !== dialogSession.current) return;
      setDeleteError(err.response?.data?.message || err.message || 'Failed to change the setting');
    } finally {
      if (session === dialogSession.current) setSavingPurgeSwitch(false);
    }
  };

  const runDeletion = async (kind: 'anonymize' | 'purge') => {
    if (!deleteTarget) return;
    if (kind === 'purge' && savingPurgeSwitch) return;
    setDeleting(true); setDeleteError(null);
    try {
      if (kind === 'anonymize') await anonymizeUser(deleteTarget.id, getToken());
      else await purgeUser(deleteTarget.id, confirmEmail, getToken());
      notify(kind === 'anonymize' ? 'Anonymized' : 'Deleted');
      dialogSession.current++;
      setDeleteTarget(null); fetchUsers();
    } catch (err: any) { setDeleteError(err.response?.data?.message || err.message || 'Deletion failed'); }
    finally { setDeleting(false); }
  };

  const purgeBlocked = !impact?.purgeEnabled || savingPurgeSwitch || deleting;

  const AccountCell: React.FC<{ u: UserAdmin }> = ({ u }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <StatusText ok={u.isActive} yes="Active" no="Inactive" />
      {u.anonymized && <AnonymizedLabel />}
      {u.role !== 'ADMIN' && !u.anonymized && (u.isActive
        ? <button onClick={() => handleDeactivate(u.id)} className="app-btn app-btn-compact app-btn-red">Deactivate</button>
        : <button onClick={() => handleActivate(u.id)}   className="app-btn app-btn-compact app-btn-green">Activate</button>
      )}
    </div>
  );

  const EmailCell: React.FC<{ u: UserAdmin }> = ({ u }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <StatusText ok={u.emailConfirmed} yes="Verified" no="Pending" />
      {!u.emailConfirmed && (
        <button onClick={() => handleVerify(u.id)} className="app-btn app-btn-compact" style={{ background: '#2b6cb0' }}>Verify</button>
      )}
    </div>
  );

  const ActionRow: React.FC<{ u: UserAdmin }> = ({ u }) => (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Link to={`/admin/users/${u.id}`} className="app-btn app-btn-compact app-btn-blue" style={{ textDecoration: 'none' }}>Detail</Link>
      {u.role !== 'ADMIN' && !u.anonymized && (
        <button onClick={() => handleDelete(u)} className="app-btn app-btn-compact app-btn-red">Delete</button>
      )}
    </div>
  );

  return (
    <div className="page-container">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#1a202c' }}>Users</h1>
          <p style={{ color: '#718096', fontSize: '0.82rem', margin: '2px 0 0' }}>Managers self-register. Admins created manually.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="app-btn app-btn-compact app-btn-blue">
          {showForm ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {/* Toasts */}
      {success && <div style={{ padding: '7px 12px', background: '#f0fff4', color: '#276749', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem', fontWeight: 600 }}>{success}</div>}
      {error   && <div style={{ padding: '7px 12px', background: '#fff5f5', color: '#c53030', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem' }}>{error}</div>}

      {/* Create form */}
      {showForm && (
        <div className="form-card" style={{ marginBottom: 14 }}>
          <form onSubmit={handleCreateUser}>
            <div className="form-grid" style={{ marginBottom: 10 }}>
              <div className="form-field">
                <label className="form-label">Email</label>
                <input className="form-control" name="email" type="email" value={formData.email} onChange={handleFormChange} required placeholder="user@example.com" />
              </div>
              <div className="form-field">
                <label className="form-label">Password</label>
                <input className="form-control" name="password" type="password" value={formData.password} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">First Name</label>
                <input className="form-control" name="firstName" type="text" value={formData.firstName} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">Last Name</label>
                <input className="form-control" name="lastName" type="text" value={formData.lastName} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">Phone</label>
                <input className="form-control" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleFormChange} />
              </div>
              <div className="form-field">
                <label className="form-label">Role</label>
                <select className="form-control" name="role" value={formData.role} onChange={handleFormChange} required>
                  <option value="MANAGER">Manager</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-sm btn-muted">Cancel</button>
              <button type="submit" className="app-btn app-btn-compact app-btn-green">Create</button>
            </div>
          </form>
        </div>
      )}

      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#718096', fontSize: '0.875rem' }}>Loading...</div>}

      {!loading && users.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="desktop-only table-scroll" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {([
                    { label: 'ID' }, { label: 'Email' }, { label: 'Name', field: 'name' }, { label: 'Role' },
                    { label: 'Account' }, { label: 'Email' }, { label: 'Apts', field: 'apartments' },
                    { label: 'Last login', field: 'lastLogin' }, { label: 'Last seen' }, { label: 'Form visit' }, { label: 'Actions' },
                  ] as { label: string; field?: UserSortField }[]).map((h, i) => (
                    <th
                      key={`${h.label}-${i}`}
                      aria-sort={h.field ? (sort?.field === h.field ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                      style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#4a5568', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
                    >
                      {h.field ? sortHeader(h.field) : h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '6px 10px', color: '#a0aec0', fontFamily: 'monospace', fontSize: '0.78rem' }}>{u.id}</td>
                    <td style={{ padding: '6px 10px', color: '#2d3748', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</td>
                    <td style={{ padding: '6px 10px', fontWeight: 500, whiteSpace: 'nowrap' }}>{u.firstName} {u.lastName}</td>
                    <td style={{ padding: '6px 10px' }}><RoleBadge role={u.role} /></td>
                    <td style={{ padding: '6px 10px' }}><AccountCell u={u} /></td>
                    <td style={{ padding: '6px 10px' }}><EmailCell u={u} /></td>
                    <td style={{ padding: '6px 10px', color: '#718096', textAlign: 'center' }}>{u.apartmentCount}</td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '0.78rem', color: u.lastLoginAt ? '#2d3748' : '#a0aec0', fontWeight: u.lastLoginAt ? 500 : 400 }}>
                        {formatLastLogin(u.lastLoginAt)}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '0.78rem', color: u.lastSeenAt ? '#2d3748' : '#a0aec0', fontWeight: u.lastSeenAt ? 500 : 400 }}>
                        {formatLastSeen(u.lastSeenAt)}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '0.78rem', color: u.lastFormVisitAt ? '#276749' : '#a0aec0', fontWeight: u.lastFormVisitAt ? 500 : 400 }}>
                        {formatLastSeen(u.lastFormVisitAt)}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px' }}><ActionRow u={u} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only">
            {/* No column headers on mobile, so the same sort state is driven from here. */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <label htmlFor="user-sort-field" style={{ fontSize: '0.78rem', color: '#4a5568', fontWeight: 600 }}>Sort by</label>
              <select
                id="user-sort-field"
                className="form-control"
                style={{ flex: 1 }}
                value={sort?.field ?? ''}
                onChange={e => (e.target.value
                  ? applySort({ field: e.target.value as UserSortField, dir: sort?.dir ?? 'asc' })
                  : clearSort())}
              >
                <option value="">Default</option>
                {(Object.keys(SORT_LABELS) as UserSortField[]).map(f => <option key={f} value={f}>{SORT_LABELS[f]}</option>)}
              </select>
              <button
                type="button"
                className="app-btn app-btn-compact"
                disabled={!sort}
                aria-label={sort?.dir === 'desc' ? 'Descending' : 'Ascending'}
                onClick={() => sort && applySort({ field: sort.field, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
              >
                {sort?.dir === 'desc' ? '▼' : '▲'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {users.map(u => (
                <div key={u.id} style={{ background: '#fff', borderRadius: 8, padding: '11px 13px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                  {/* Name + email | id + role */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1a202c', fontSize: '0.9rem' }}>{u.firstName} {u.lastName}</div>
                      <div style={{ color: '#718096', fontSize: '0.78rem', marginTop: 1 }}>{u.email}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, marginLeft: 8 }}>
                      <span style={{ color: '#a0aec0', fontFamily: 'monospace', fontSize: '0.72rem' }}>#{u.id}</span>
                      <RoleBadge role={u.role} />
                    </div>
                  </div>

                  {/* Status pills — info only, no buttons */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                    <StatusPill ok={u.isActive} yes="Active" no="Inactive" />
                    {u.anonymized && <AnonymizedLabel />}
                    <StatusPill ok={u.emailConfirmed} yes="Verified" no="Pending" />
                    {u.apartmentCount > 0 && <span style={{ color: '#a0aec0', fontSize: '0.72rem' }}>{u.apartmentCount} apt{u.apartmentCount !== 1 ? 's' : ''}</span>}
                    <span style={{ color: u.lastLoginAt ? '#718096' : '#a0aec0', fontSize: '0.72rem' }}>🔑 {formatLastLogin(u.lastLoginAt)}</span>
                    <span style={{ color: u.lastSeenAt ? '#718096' : '#a0aec0', fontSize: '0.72rem' }}>👁 {formatLastSeen(u.lastSeenAt)}</span>
                    <span style={{ color: u.lastFormVisitAt ? '#276749' : '#a0aec0', fontSize: '0.72rem' }}>📋 {formatLastSeen(u.lastFormVisitAt)}</span>
                  </div>

                  {/* All action buttons in one row */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {u.role !== 'ADMIN' && !u.anonymized && (u.isActive
                      ? <button onClick={() => handleDeactivate(u.id)} className="app-btn app-btn-compact app-btn-red">Deactivate</button>
                      : <button onClick={() => handleActivate(u.id)}   className="app-btn app-btn-compact app-btn-green">Activate</button>
                    )}
                    {!u.emailConfirmed && (
                      <button onClick={() => handleVerify(u.id)} className="app-btn app-btn-compact" style={{ background: '#2b6cb0' }}>Verify</button>
                    )}
                    <Link to={`/admin/users/${u.id}`} className="app-btn app-btn-compact app-btn-blue" style={{ textDecoration: 'none' }}>Detail</Link>
                    {u.role !== 'ADMIN' && !u.anonymized && (
                      <button onClick={() => handleDelete(u)} className="app-btn app-btn-compact app-btn-red" title="Delete user" style={{ padding: '0 9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Delete user modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>Delete user</h2>
            <p style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 12px', wordBreak: 'break-all' }}>{deleteTarget.email}</p>

            {impactError && <div style={{ padding: '7px 12px', background: '#fff5f5', color: '#c53030', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem' }}>{impactError}</div>}
            {!impact && !impactError && <div style={{ color: '#718096', fontSize: '0.85rem', marginBottom: 12 }}>Loading...</div>}
            {impact && (
              <ul style={{ color: '#4a5568', fontSize: '0.85rem', margin: '0 0 16px', paddingLeft: 18 }}>
                <li>{impact.apartments} apartment(s), {impact.reservations} reservation(s)</li>
                <li>{impact.billingRecords} invoice(s)</li>
                <li>{impact.contacts} contact(s), {impact.cleaners} cleaner(s), {impact.paymentMethods} payment method(s)</li>
                <li>{impact.pmsConnections} PMS connection(s){impact.hasStripeAccount ? ', connected Stripe account' : ''}</li>
              </ul>
            )}

            {deleteError && <div style={{ padding: '7px 12px', background: '#fff5f5', color: '#c53030', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem' }}>{deleteError}</div>}

            <div style={{ marginBottom: 18 }}>
              <button
                onClick={() => runDeletion('anonymize')}
                disabled={!impact || !!impact.anonymizeBlockedReason || deleting}
                style={{ width: '100%', padding: '10px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: !impact || impact.anonymizeBlockedReason || deleting ? 'not-allowed' : 'pointer', opacity: !impact || impact.anonymizeBlockedReason || deleting ? 0.5 : 1 }}
              >
                Anonymize (keep invoices)
              </button>
              <p style={{ color: '#718096', fontSize: '0.78rem', margin: '6px 0 0' }}>
                Removes personal data and signs the user out for good. Invoices are kept.
              </p>
              {impact?.anonymizeBlockedReason && (
                <p style={{ color: '#c53030', fontSize: '0.78rem', margin: '4px 0 0' }}>{impact.anonymizeBlockedReason}</p>
              )}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', marginBottom: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!impact?.purgeEnabled}
                  disabled={!impact || savingPurgeSwitch || deleting}
                  onChange={e => togglePurgeEnabled(e.target.checked)}
                />
                Allow "Delete everything" on this environment (off by default)
              </label>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>
                Type the user's email to enable full deletion
              </label>
              <input
                type="text"
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
                placeholder={deleteTarget.email}
                autoComplete="off"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: 8 }}
              />
              <button
                onClick={() => runDeletion('purge')}
                disabled={purgeBlocked || confirmEmail !== deleteTarget.email}
                style={{ width: '100%', padding: '10px', background: '#c53030', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: purgeBlocked || confirmEmail !== deleteTarget.email ? 'not-allowed' : 'pointer', opacity: purgeBlocked || confirmEmail !== deleteTarget.email ? 0.5 : 1 }}
              >
                Delete everything (test accounts)
              </button>
              <p style={{ color: '#718096', fontSize: '0.78rem', margin: '6px 0 0' }}>
                For test accounts. Permanently deletes apartments, reservations, guest data and invoices. A connected Stripe account stays at Stripe.
              </p>
            </div>

            <button onClick={closeDeleteModal} disabled={deleting} style={{ padding: '10px 16px', background: '#f7fafc', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
